import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircleIcon, XIcon, AlertCircleIcon, LinkIcon, ChevronRightIcon, ArrowRightIcon, PartyPopperIcon, ClockIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { Product } from '../utils/mockData';
const ProductRow = ({
  product,
  type
}: {
  product: Product;
  type: 'link' | 'skip';
}) => {
  return <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200" data-id="element-250">
      <div className="w-12 h-12 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0" data-id="element-251">
        <img src={product.imageUrl} alt={product.title} className="w-full h-full object-cover" data-id="element-252" />
      </div>
      <div className="flex-1 min-w-0" data-id="element-253">
        <h4 className="text-sm font-medium text-gray-900 truncate" data-id="element-254">
          {product.title}
        </h4>
        <p className="text-sm text-gray-500" data-id="element-255">
          SKU: {product.sku} • ${product.price.toFixed(2)}
        </p>
      </div>
      <div className={`flex items-center ${type === 'link' ? 'text-blue-600' : 'text-purple-600'}`} data-id="element-256">
        {type === 'link' ? <LinkIcon className="h-4 w-4" data-id="element-257" /> : <ClockIcon className="h-4 w-4" data-id="element-258" />}
      </div>
    </div>;
};
interface ReviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  products: Product[];
  skippedProducts?: Product[];
  isLoading?: boolean;
  remainingCount?: {
    potentialMatch: number;
    noMatch: number;
  };
  isSuccess?: boolean;
}
const ReviewModal = ({
  isOpen,
  onClose,
  onConfirm,
  products,
  skippedProducts = [],
  isLoading = false,
  remainingCount,
  isSuccess = false
}: ReviewModalProps) => {
  const [isSuccessState, setIsSuccessState] = useState(isSuccess);
  const navigate = useNavigate();
  useEffect(() => {
    setIsSuccessState(isSuccess);
  }, [isSuccess]);
  const handleConfirm = () => {
    onConfirm();
  };
  const handleContinue = (path: string) => {
    navigate(path);
  };
  const hasLinkedProducts = products.length > 0;
  const hasSkippedProducts = skippedProducts.length > 0;
  const totalProductCount = products.length + skippedProducts.length;
  const getActionText = () => {
    if (hasLinkedProducts && hasSkippedProducts) {
      return 'Process All Changes';
    }
    if (hasLinkedProducts) {
      return 'Confirm & Link Products';
    }
    if (hasSkippedProducts) {
      return 'Confirm & Skip Products';
    }
    return 'Confirm Changes';
  };
  const getLoadingText = () => {
    if (hasLinkedProducts && hasSkippedProducts) {
      return 'Processing Changes...';
    }
    if (hasLinkedProducts) {
      return 'Linking Products...';
    }
    if (hasSkippedProducts) {
      return 'Skipping Products...';
    }
    return 'Processing...';
  };
  const getSuccessMessage = () => {
    if (hasLinkedProducts && hasSkippedProducts) {
      return {
        title: 'Changes Successfully Processed!',
        description: `${products.length} ${products.length === 1 ? 'product' : 'products'} linked and ${skippedProducts.length} ${skippedProducts.length === 1 ? 'product' : 'products'} skipped.`
      };
    }
    if (hasLinkedProducts) {
      return {
        title: 'Products Successfully Linked!',
        description: 'Your products are now linked. Please wait 15 minutes to see updates in manage your products.'
      };
    }
    if (hasSkippedProducts) {
      return {
        title: 'Products Successfully Skipped!',
        description: 'Your products have been moved to the skipped section for later review.'
      };
    }
    return {
      title: 'Changes Processed!',
      description: 'Your changes have been saved successfully.'
    };
  };
  if (!isOpen) return null;
  return <AnimatePresence data-id="element-259">
      <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-260">
        <div className="min-h-screen text-center px-4" data-id="element-261">
          <span className="inline-block h-screen align-middle" aria-hidden="true" data-id="element-262">
            &#8203;
          </span>
          <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 bg-black bg-opacity-30" onClick={!isLoading ? onClose : undefined} data-id="element-263" />
          <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} className="inline-block w-full max-w-2xl my-8 text-left align-middle relative rounded-xl bg-white shadow-lg overflow-hidden" data-id="element-264">
            {isSuccessState ? <div className="p-6" data-id="element-265">
                <div className="text-center" data-id="element-266">
                  <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200,
                damping: 15
              }} className="mx-auto mb-4 h-12 w-12 rounded-full bg-green-100 flex items-center justify-center" data-id="element-267">
                    <PartyPopperIcon className="h-6 w-6 text-green-600" data-id="element-268" />
                  </motion.div>
                  <h2 className="text-xl font-semibold text-gray-900 mb-2" data-id="element-269">
                    {getSuccessMessage().title}
                  </h2>
                  <p className="text-sm text-gray-500 mb-6" data-id="element-270">
                    {getSuccessMessage().description}
                  </p>
                  <div className="mt-6 p-4 rounded-lg bg-blue-50 border border-blue-100" data-id="element-271">
                    <div className="flex items-start" data-id="element-272">
                      <AlertCircleIcon className="h-5 w-5 text-blue-600 mt-0.5" data-id="element-273" />
                      <div className="ml-3 text-left" data-id="element-274">
                        <h3 className="text-sm font-medium text-blue-900" data-id="element-275">
                          What's Next?
                        </h3>
                        <p className="mt-2 text-sm text-blue-700" data-id="element-276">
                          {hasLinkedProducts && <>
                              • Linked products will appear in your Amazon
                              catalog within 15 minutes
                              <br data-id="element-277" />
                              • You'll receive a notification when the process
                              is complete
                              <br data-id="element-278" />
                            </>}
                          {hasSkippedProducts && <>
                              • Skipped products can be found in the "Skipped"
                              section
                              <br data-id="element-279" />
                              • Review them again when you're ready
                              <br data-id="element-280" />
                            </>}
                          • You can continue{' '}
                          {hasLinkedProducts ? 'linking' : 'reviewing'} more
                          products
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="mt-6 space-y-3" data-id="element-281">
                    <motion.button onClick={() => handleContinue('/')} className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700" whileHover={{
                  scale: 1.02
                }} whileTap={{
                  scale: 0.98
                }} data-id="element-282">
                      Return to Dashboard
                    </motion.button>
                    <button onClick={onClose} className="w-full px-4 py-2 text-gray-600 hover:text-gray-900" data-id="element-283">
                      Continue Linking Products
                    </button>
                  </div>
                </div>
              </div> : <>
                <div className="border-b border-gray-200 p-4" data-id="element-284">
                  <div className="flex items-center justify-between" data-id="element-285">
                    <div className="flex items-center space-x-2" data-id="element-286">
                      <div className="rounded-full bg-blue-100 p-2" data-id="element-287">
                        <LinkIcon className="h-5 w-5 text-blue-600" data-id="element-288" />
                      </div>
                      <div data-id="element-289">
                        <h2 className="text-lg font-semibold text-gray-900" data-id="element-290">
                          Review Changes
                        </h2>
                        <p className="text-sm text-gray-500" data-id="element-291">
                          {products.length > 0 && skippedProducts.length > 0 ? `${products.length} to link • ${skippedProducts.length} to skip` : 'Confirm the products you want to link to Amazon'}
                        </p>
                      </div>
                    </div>
                    <button onClick={onClose} className="rounded-full p-2 hover:bg-gray-100" data-id="element-292">
                      <XIcon className="h-5 w-5 text-gray-400" data-id="element-293" />
                    </button>
                  </div>
                </div>
                <div className="max-h-[60vh] overflow-y-auto p-4" data-id="element-294">
                  {products.length > 0 && <div className="space-y-4" data-id="element-295">
                      <div className="flex items-center" data-id="element-296">
                        <LinkIcon className="h-4 w-4 text-blue-500 mr-2" data-id="element-297" />
                        <h3 className="text-sm font-medium text-gray-900" data-id="element-298">
                          Products to Link
                        </h3>
                      </div>
                      {products.map(product => <ProductRow key={product.id} product={product} type="link" data-id="element-299" />)}
                    </div>}
                  {skippedProducts.length > 0 && <div className="mt-6 space-y-3" data-id="element-300">
                      <div className="flex items-center" data-id="element-301">
                        <ClockIcon className="h-4 w-4 text-purple-500 mr-2" data-id="element-302" />
                        <h3 className="text-sm font-medium text-gray-900" data-id="element-303">
                          Products to Skip
                        </h3>
                      </div>
                      {skippedProducts.map(product => <ProductRow key={product.id} product={product} type="skip" data-id="element-304" />)}
                    </div>}
                  <div className="mt-6 rounded-lg bg-blue-50 p-4" data-id="element-305">
                    <div className="flex items-start" data-id="element-306">
                      <AlertCircleIcon className="h-5 w-5 text-blue-600" data-id="element-307" />
                      <div className="ml-3" data-id="element-308">
                        <h3 className="text-sm font-medium text-blue-800" data-id="element-309">
                          Summary
                        </h3>
                        <div className="mt-2 text-sm text-blue-700 space-y-1" data-id="element-310">
                          {hasLinkedProducts && <p data-id="element-311">Products to link: {products.length}</p>}
                          {hasSkippedProducts && <p data-id="element-312">Products to skip: {skippedProducts.length}</p>}
                          <p className="mt-2" data-id="element-313">
                            Total changes: {totalProductCount}{' '}
                            {totalProductCount === 1 ? 'product' : 'products'}
                          </p>
                          {hasLinkedProducts && <p data-id="element-314">
                              Total product value: $
                              {products.reduce((sum, product) => sum + product.price, 0).toFixed(2)}
                            </p>}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="border-t border-gray-200 p-4" data-id="element-315">
                  <div className="flex items-center justify-end space-x-3" data-id="element-316">
                    <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-900" disabled={isLoading} data-id="element-317">
                      Cancel
                    </button>
                    <motion.button onClick={handleConfirm} disabled={isLoading} className={`flex items-center rounded-md bg-blue-600 px-4 py-2 text-sm text-white ${isLoading ? 'opacity-75' : 'hover:bg-blue-700'}`} whileHover={!isLoading ? {
                  scale: 1.02
                } : {}} whileTap={!isLoading ? {
                  scale: 0.98
                } : {}} data-id="element-318">
                      {isLoading ? <>
                          <motion.div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" animate={{
                      rotate: 360
                    }} transition={{
                      duration: 1,
                      repeat: Infinity,
                      ease: 'linear'
                    }} data-id="element-319" />
                          {getLoadingText()}
                        </> : <>
                          <CheckCircleIcon className="mr-2 h-4 w-4" data-id="element-320" />
                          {getActionText()}
                        </>}
                    </motion.button>
                  </div>
                </div>
              </>}
          </motion.div>
        </div>
      </div>
    </AnimatePresence>;
};
export default ReviewModal;